/* Shop bundle (sections/cs-shop-bundle.liquid)
   <cs-shop-bundle>: keeps the active step card and its hotspot on the image
   in sync (click either, use the arrow, or swipe the row), and adds the
   bundle product to the cart.

   The row loops: a copy of every card sits before and after the real ones.
   When the row comes to rest on a copy, it jumps (instantly, so nothing
   moves on screen) to the matching real card, so it can keep going in
   either direction forever.
*/

if (!customElements.get('cs-shop-bundle')) {
    class CsShopBundle extends HTMLElement {
        constructor() {
            super();
            this.activeIndex = 0;
        }

        connectedCallback() {
            this.track = this.querySelector('[data-track]');
            this.hotspots = Array.from(this.querySelectorAll('[data-hotspot]'));
            this.advanceButton = this.querySelector('[data-advance]');
            this.addButton = this.querySelector('[data-add-bundle]');
            this.errorMessage = this.querySelector('[data-error]');

            if (this.track) this.setUpTrack();

            this.addButton?.addEventListener('click', () => this.addToCart());
        }

        setUpTrack() {
            this.realSteps = Array.from(this.track.children);

            if (this.realSteps.length > 1) {
                this.track.prepend(...this.realSteps.map((step) => this.cloneStep(step)));
                this.track.append(...this.realSteps.map((step) => this.cloneStep(step)));
            }

            this.steps = Array.from(this.track.children);
            this.cards = this.steps.map((step) => step.querySelector('[data-step]'));

            this.steps.forEach((step) => {
                step.querySelector('[data-step]').addEventListener('click', () => this.scrollToStep(step));
            });

            this.hotspots.forEach((hotspot) => {
                hotspot.addEventListener('click', () => {
                    this.scrollToStep(this.realSteps[Number(hotspot.dataset.hotspot)]);
                });
            });

            // The one arrow moves to the next card, forever
            this.advanceButton?.addEventListener('click', () => {
                const next = this.steps[this.steps.indexOf(this.getClosestStep()) + 1];
                if (next) this.scrollToStep(next);
            });

            // Once the row stops (after the arrow, a click or a swipe), the
            // card at its left edge is the active step
            this.track.addEventListener('scroll', debounce(() => this.onScrollSettle(), 120));

            // Theme editor: selecting a Step block shows that step
            document.addEventListener('shopify:block:select', (event) => {
                const index = this.realSteps.findIndex(
                    (step) => step.querySelector('[data-step]').dataset.blockId === event.detail.blockId
                );
                if (index !== -1) this.scrollToStep(this.realSteps[index]);
            });

            // Start on the first real card, past the copies before it
            this.setActive(0);
            requestAnimationFrame(() => this.jumpTo(this.realSteps[0]));
        }

        cloneStep(step) {
            const clone = step.cloneNode(true);
            clone.dataset.clone = '';
            clone.setAttribute('aria-hidden', 'true');
            Array.from(clone.attributes)
                .filter((attribute) => attribute.name.startsWith('data-shopify'))
                .forEach((attribute) => clone.removeAttribute(attribute.name));
            clone.querySelectorAll('button').forEach((button) => {
                button.tabIndex = -1;
                button.removeAttribute('data-block-id');
            });
            return clone;
        }

        stepIndex(step) {
            return Number(step.querySelector('[data-step]').dataset.step);
        }

        setActive(index) {
            this.activeIndex = index;

            this.cards.forEach((card) => {
                const isActive = Number(card.dataset.step) === index;
                card.classList.toggle('is-active', isActive);
                if (!card.closest('[data-clone]')) card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });

            this.hotspots.forEach((hotspot) => {
                const isActive = Number(hotspot.dataset.hotspot) === index;
                hotspot.classList.toggle('is-active', isActive);
                hotspot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        }

        scrollToStep(step) {
            if (!step) return;
            this.setActive(this.stepIndex(step));
            this.track.scrollTo({ left: step.offsetLeft, behavior: 'smooth' });
        }

        // Moves without animating, e.g. from a copy to its real card
        jumpTo(step) {
            if (!step) return;
            this.track.scrollTo({ left: step.offsetLeft, behavior: 'instant' });
        }

        onScrollSettle() {
            const step = this.getClosestStep();
            if (!step) return;

            const index = this.stepIndex(step);
            if (step.hasAttribute('data-clone')) {
                // Keep the same offset from the card, so the jump is invisible
                const offset = this.track.scrollLeft - step.offsetLeft;
                this.track.scrollTo({ left: this.realSteps[index].offsetLeft + offset, behavior: 'instant' });
            }
            this.setActive(index);
        }

        getClosestStep() {
            const scrollLeft = this.track.scrollLeft;
            let closest = null;
            let closestDistance = Infinity;
            this.steps.forEach((step) => {
                const distance = Math.abs(step.offsetLeft - scrollLeft);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closest = step;
                }
            });
            return closest;
        }

        async addToCart() {
            const variantId = this.addButton.dataset.variantId;
            if (!variantId || this.addButton.disabled) return;

            this.addButton.classList.add('loading');
            this.addButton.setAttribute('aria-disabled', 'true');
            this.showError('');

            try {
                // Adds the bundle and opens the cart drawer (assets/pro-cart.js)
                await ProCart.add([{ id: Number(variantId), quantity: 1 }], { source: 'cs-shop-bundle' });
            } catch (error) {
                console.error('Error adding the bundle to the cart:', error);
                this.showError(error.message || ProCart.ERROR_MESSAGE);
            } finally {
                this.addButton.classList.remove('loading');
                this.addButton.removeAttribute('aria-disabled');
            }
        }

        showError(message) {
            if (!this.errorMessage) return;
            this.errorMessage.textContent = message;
            this.errorMessage.hidden = !message;
        }
    }

    customElements.define('cs-shop-bundle', CsShopBundle);
}
